type TelegramWebAppUser = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
};

type TelegramWebApp = {
  initData: string;
  initDataUnsafe: {
    user?: TelegramWebAppUser;
  };
  platform?: string;
  version?: string;
  ready: () => void;
  expand: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  colorScheme?: 'light' | 'dark';
  showConfirm?: (message: string, callback: (confirmed: boolean) => void) => void;
};

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

let cachedInitData = '';

export function looksLikeTelegramEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.Telegram?.WebApp) return true;
  const ua = navigator.userAgent || '';
  if (/Telegram/i.test(ua)) return true;
  try {
    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    if (params.get('tgWebAppData') || params.get('tgWebAppVersion')) return true;
  } catch {
    /* ignore */
  }
  return false;
}

export function loadTelegramWebAppScript(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Telegram?.WebApp) return Promise.resolve(true);
  return new Promise((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-yp-tg-webapp]');
    if (existing) {
      const done = () => resolve(Boolean(window.Telegram?.WebApp));
      if (window.Telegram?.WebApp) {
        done();
        return;
      }
      existing.addEventListener('load', done);
      existing.addEventListener('error', () => resolve(false));
      window.setTimeout(done, 2500);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-web-app.js';
    script.async = true;
    script.dataset.ypTgWebapp = '1';
    script.onload = () => resolve(Boolean(window.Telegram?.WebApp));
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
    window.setTimeout(() => resolve(Boolean(window.Telegram?.WebApp)), 4000);
  });
}

export function getTelegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function isTelegramWebApp(): boolean {
  const webApp = getTelegramWebApp();
  if (!webApp) return false;
  if (webApp.initData) return true;
  if (webApp.initDataUnsafe?.user?.id) return true;
  const platform = webApp.platform?.toLowerCase();
  return Boolean(platform && platform !== 'unknown');
}

export function getTelegramInitData(): string {
  const live = getTelegramWebApp()?.initData ?? '';
  if (live) return live;
  return cachedInitData;
}

export function setCachedTelegramInitData(initData: string): void {
  cachedInitData = initData;
}

/** Wait for Telegram.WebApp.initData (never use URL hash — format differs). */
export async function resolveTelegramInitData(timeoutMs = 5000): Promise<string> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const live = getTelegramWebApp()?.initData ?? '';
    if (live) {
      cachedInitData = live;
      return live;
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  return getTelegramWebApp()?.initData ?? '';
}

export function initTelegramWebApp(): void {
  const webApp = getTelegramWebApp();
  if (!webApp) return;

  webApp.ready();
  webApp.expand();
  webApp.setHeaderColor?.('#0f172a');
  webApp.setBackgroundColor?.('#0b1220');

  if (webApp.colorScheme === 'dark') {
    document.documentElement.dataset.telegramTheme = 'dark';
  }
}

/** Reload data when user returns to the mini app or browser tab. */
export function onAppResume(callback: () => void): () => void {
  const onVisible = () => {
    if (document.visibilityState === 'visible') callback();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', callback);
  window.addEventListener('focus', callback);
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', callback);
    window.removeEventListener('focus', callback);
  };
}
